import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { LiveNowBanner } from "@/components/LiveNowBanner";
import { Footer } from "@/components/Footer";
import { PostCard } from "@/components/PostCard";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PenLine, Star } from "lucide-react";
import { UpcomingBroadcasts } from "@/components/UpcomingBroadcasts";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { EditorsLetterStrip } from "@/components/EditorsLetterStrip";
import { getSection } from "@/lib/sections";
import { useContent } from "@/context/SiteContentContext";

const Feed = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [sections, setSections] = useState([]);
  const [newInRooms, setNewInRooms] = useState(0);
  const [favRooms, setFavRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  const feedOverline = useContent("feed.hero.overline", "Today in the salon");
  const feedBody = useContent("feed.hero.body", "A few quiet, beautiful things our members shared today.");

  const load = async () => {
    setLoading(true);
    try {
      const [feed, users, secs] = await Promise.all([
        api.get("/feed/personalized"),
        api.get("/users", { params: { limit: 6 } }),
        api.get("/sections"),
      ]);
      setPosts(feed.data.posts || []);
      setNewInRooms(feed.data.new_in_rooms || 0);
      setFavRooms(feed.data.favorite_rooms || []);
      setSuggested((users.data || []).filter((u) => u.id !== user?.id).slice(0, 5));
      setSections(secs.data || []);
      // Mark rooms as seen after a brief delay so the badge is visible on first arrival
      setTimeout(() => { api.post("/users/me/rooms-seen").catch(() => {}); }, 1500);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div data-testid="feed-page" className="app-shell">
      <Navbar />
      <LiveNowBanner />

      <div style={{ paddingTop: 110, paddingBottom: 48 }}>
        <div className="container-belle">
          <EditorsLetterStrip />
        </div>
        <div className="container-belle" style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 56 }}>
          {/* MAIN COLUMN */}
          <div>
            <div style={{ marginBottom: 40 }}>
              <div className="overline" style={{ color: "var(--brand-accent)" }}>{feedOverline}</div>
              <h1 className="editorial-h1" style={{ fontSize: "clamp(40px, 5.5vw, 64px)", marginTop: 12 }}>
                {greeting()}, {user?.display_name?.split(" ")[0] || "ma chère"}.
              </h1>
              <p className="editorial-body" style={{ fontSize: 17, marginTop: 12, maxWidth: 540 }}>
                {feedBody}
              </p>
            </div>

            {/* "New in your rooms" badge */}
            {favRooms.length > 0 && newInRooms > 0 && (
              <Link
                to="/rooms"
                data-testid="new-in-rooms-badge"
                style={{
                  display: "flex", alignItems: "center", gap: 14,
                  padding: "14px 20px",
                  background: "var(--brand-blush)",
                  border: "1px solid var(--brand-blush)",
                  marginBottom: 24,
                  textDecoration: "none",
                }}
              >
                <span style={{ display: "inline-flex", width: 32, height: 32, borderRadius: "50%", background: "var(--brand-accent)", color: "white", justifyContent: "center", alignItems: "center", fontSize: 13, fontWeight: 600 }}>
                  {newInRooms}
                </span>
                <div style={{ flex: 1 }}>
                  <div className="font-serif" style={{ fontSize: 17, fontStyle: "italic", color: "var(--text-primary)" }}>
                    New in your favorite {favRooms.length === 1 ? "room" : "rooms"} since you last visited.
                  </div>
                </div>
                <span style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--brand-accent)", fontWeight: 500 }}>
                  See →
                </span>
              </Link>
            )}

            {/* Compose hint */}
            <Link
              to="/compose"
              data-testid="feed-compose-cta"
              style={{
                display: "flex", alignItems: "center", gap: 16, padding: "20px 24px",
                border: "1px solid var(--border-default)", marginBottom: 40,
                background: "var(--surface)", textDecoration: "none",
              }}
            >
              {user?.avatar_url ? (
                <img loading="lazy" decoding="async" src={user.avatar_url} alt="" style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover" }} />
              ) : (
                <div style={{ width: 44, height: 44, borderRadius: "50%", background: "var(--bg-alt)" }} />
              )}
              <span style={{ flex: 1, color: "var(--text-muted)", fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: 19 }}>
                Share something beautiful today…
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--brand-accent)" }}>
                <PenLine size={16} strokeWidth={1.4} /> Publish
              </span>
            </Link>

            {/* THIS WEEK'S PICKS STRIP */}
            {sections.some((s) => s.featured_post) && (
              <section data-testid="picks-strip" style={{ marginBottom: 48, padding: "28px 24px", background: "var(--bg-alt)", border: "1px solid var(--border-default)" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Star size={16} strokeWidth={1.4} fill="var(--brand-accent)" color="var(--brand-accent)" />
                    <div className="overline" style={{ color: "var(--brand-accent)" }}>This week's picks</div>
                  </div>
                  <Link to="/editors-letter" className="link-underline" style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--brand-accent)", fontWeight: 500 }}>
                    The Letter →
                  </Link>
                </div>
                <div style={{ display: "flex", gap: 14, overflowX: "auto", paddingBottom: 4 }}>
                  {sections.filter((s) => s.featured_post).map((s) => {
                    const meta = getSection(s.slug);
                    const p = s.featured_post;
                    return (
                      <Link
                        key={s.slug}
                        to={`/post/${p.id}`}
                        data-testid={`pick-${s.slug}`}
                        className="media-zoom"
                        style={{
                          flex: "0 0 200px", display: "block",
                          background: "var(--surface)", border: "1px solid var(--border-default)",
                          textDecoration: "none",
                        }}
                      >
                        <div style={{ aspectRatio: "1/1", overflow: "hidden" }}>
                          <img loading="lazy" decoding="async" src={p.media_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        </div>
                        <div style={{ padding: 12 }}>
                          <div className="overline" style={{ color: meta?.accent, fontSize: 9 }}>{meta?.label}</div>
                          <p className="font-serif" style={{ fontSize: 14, fontStyle: "italic", lineHeight: 1.35, margin: "6px 0 0", color: "var(--text-primary)" }}>
                            {p.title || p.caption?.slice(0, 60) + (p.caption?.length > 60 ? "…" : "")}
                          </p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </section>
            )}

            {loading ? (
              <p style={{ color: "var(--text-muted)" }}>Loading the salon…</p>
            ) : posts.length === 0 ? (
              <>
                <UpcomingBroadcasts variant="feed" />
                <div style={{ textAlign: "center", padding: 80, border: "1px solid var(--border-default)" }}>
                  <p className="font-serif" style={{ fontStyle: "italic", fontSize: 22 }}>The salon is quiet this morning.</p>
                  <Link to="/compose" className="btn-primary" style={{ marginTop: 20, display: "inline-block", textDecoration: "none" }}>
                    Be the first to post
                  </Link>
                </div>
              </>
            ) : (
              <>
                <UpcomingBroadcasts variant="feed" />
                {posts.map((p) => <PostCard key={p.id} post={p} onUpdate={load} />)}
              </>
            )}
          </div>

          {/* SIDEBAR */}
          <aside style={{ position: "sticky", top: 110, alignSelf: "start" }} className="feed-sidebar">
            <div style={{ padding: 24, background: "var(--bg-alt)", border: "1px solid var(--border-default)", marginBottom: 32 }}>
              <div className="overline">Membres à rencontrer</div>
              <p className="font-serif" style={{ fontSize: 22, fontStyle: "italic", marginTop: 8, marginBottom: 20, lineHeight: 1.3 }}>
                Women to meet today.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {suggested.map((u) => (
                  <Link
                    key={u.id}
                    to={`/profile/${u.username}`}
                    data-testid={`suggested-user-${u.username}`}
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    {u.avatar_url ? (
                      <img loading="lazy" decoding="async" src={u.avatar_url} alt={u.username} style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }} />
                    ) : (
                      <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--border-default)" }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="font-serif" style={{ fontSize: 16, fontWeight: 500 }}>{u.display_name}</div>
                      <div className="overline" style={{ marginTop: 2 }}>@{u.username}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div style={{ padding: 24, border: "1px solid var(--border-default)", marginBottom: 32 }}>
              <div className="overline" style={{ color: "var(--brand-accent)" }}>Coming soon</div>
              <p className="font-serif" style={{ fontSize: 22, fontStyle: "italic", marginTop: 8, marginBottom: 16, lineHeight: 1.3 }}>
                Live salons.
              </p>
              <p style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.6, marginBottom: 16 }}>
                Cooking with chefs in Lyon. Walks through Parisian flea markets. We are preparing the next room.
              </p>
            </div>

            <div style={{ marginBottom: 32 }}>
              <NewsletterSignup variant="compact" />
            </div>

            <div className="overline" style={{ textAlign: "center", opacity: 0.6 }}>
              In partnership with<br />Belle Inspiration · Joie de Vivre TV
            </div>
          </aside>
        </div>
      </div>

      <Footer />

      <style>{`
        @media (max-width: 1024px) {
          .feed-sidebar { display: none; }
          [data-testid="feed-page"] .container-belle { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Bonjour";
  if (h < 18) return "Bon après-midi";
  return "Bonsoir";
}

export default Feed;
